import {aiAgentRoutes} from '@ai/ai-agent/ai-agent-routes';
import {cannedRepliesRoutes} from '@app/canned-replies/canned-replies-routes';
import {customersRoutes} from '@app/dashboard/contacts/customers-routes';
import {getConversationListLayout} from '@app/dashboard/conversations/conversation-page/use-agent-inbox-layout';
import {helpdeskQueries} from '@app/dashboard/helpdesk-queries';
import {helpdeskReportRoutes} from '@app/dashboard/reports/helpdesk-reports-routes';
import {teamRoutes} from '@app/dashboard/team-routes';
import {viewsRoutes} from '@app/dashboard/views/views-routes';
import {hcManagerRoutes} from '@app/help-center/manager/hc-manager-routes';
import {shouldRevalidateDatatableLoader} from '@common/datatable/filters/utils/should-revalidate-datatable-loader';
import {PageErrorMessage} from '@common/errors/page-error-message';
import {queryClient} from '@common/http/query-client';
import {notificationSubscriptionsQueryOptions} from '@common/notifications/subscriptions/requests/notification-subscriptions';
import {campaignsRoutes} from '@livechat/dashboard/campaigns/campaigns-routes';
import {searchParamsFromUrl} from '@ui/utils/urls/search-params-from-url';
import {Outlet, redirect, RouteObject} from 'react-router';
import {AuthRoute} from '@common/auth/guards/auth-route';
import {DashboardPageFrame} from '@app/dashboard/shared-pages/dashboard-page-frame';
import {StaffPageGate, staffPageGuard} from '@common/auth/guards/staff-page-gate';
import {directoryDashboardRoutes} from '@app/opportunities/admin/directory-dashboard-routes';

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    lazy: () =>
      import('@app/dashboard/dashboard-layout/helpdesk-dashboard-layout'),
    // The team inbox. It only required being signed in, so any account —
    // a student included — could open it, and several sections below had no
    // permission check of their own. Each section now carries the same
    // permission the sidebar uses to show it; the API checks again.
    // Any staff member with at least one dashboard area; each area (and the
    // Directory) checks its own permission — config/staff-access.php.
    loader: ({request}) => staffPageGuard(request.url),
    children: [
     {
      element: <StaffPageGate />,
      children: [
      ...hcManagerRoutes,
      {element: <AuthRoute permission="reports.view" />, children: helpdeskReportRoutes},
      {
        path: 'notifications',
        lazy: () => import('@app/dashboard/agent-notification-settings-page'),
        loader: () =>
          queryClient.ensureQueryData(notificationSubscriptionsQueryOptions),
      },
      {
        // A real home page (cards per role) instead of a redirect into the
        // inbox, which most roles cannot open.
        index: true,
        lazy: () => import('@app/dashboard/home/dashboard-home-page'),
      },
      ...directoryDashboardRoutes,
      {
        path: 'conversations/new',
        handle: {customDashboardLayout: true},
        lazy: () =>
          import(
            '@app/dashboard/conversations/new-conversation-page/new-conversation-page'
          ),
        loader: async () =>
          Promise.allSettled([
            queryClient.ensureQueryData(
              helpdeskQueries.statuses.dropdownList('agent'),
            ),
            queryClient.ensureQueryData(
              helpdeskQueries.attributes.normalizedList({
                type: 'conversation',
                for: 'agent',
              }),
            ),
          ]),
      },
      {
        path: 'conversations/search',
        lazy: () =>
          import('@app/dashboard/conversations/conversations-search-page'),
        shouldRevalidate: shouldRevalidateDatatableLoader,
        loader: ({request}) => {
          const searchParams = searchParamsFromUrl(request.url);
          const promises: Promise<any>[] = [
            queryClient.ensureQueryData(
              helpdeskQueries.attributes.normalizedList({
                type: 'conversation',
                for: 'agent',
              }),
            ),
          ];

          // only load data if we have filters or search query in the url
          if (searchParams.filters || searchParams.query) {
            promises.push(
              queryClient.ensureQueryData(
                helpdeskQueries.conversations.search(searchParams),
              ),
            );
          }

          return Promise.allSettled(promises);
        },
      },
      {
        handle: {customDashboardLayout: true},
        loader: async ({request, params}) => {
          const url = new URL(request.url);
          const selectedLayout = getConversationListLayout();

          // fetch views and list of conversations for active view
          const data = await Promise.allSettled([
            queryClient.ensureQueryData(
              helpdeskQueries.conversations.inboxViews,
            ),
            queryClient.ensureQueryData(
              helpdeskQueries.conversations.index(
                Object.fromEntries(url.searchParams),
              ),
            ),
          ]);

          // if there's an error loading data, show error page
          if (data[0].status === 'rejected' || data[1].status === 'rejected') {
            return <PageErrorMessage />;
          }

          const firstConversation = data[1].value.pagination.data[0];
          let redirectUrl: URL | null = null;

          // default to "all" view
          if (!url.searchParams.has('viewId')) {
            url.searchParams.append('viewId', 'all');
            redirectUrl = url;
          }

          // if layout is set to chat, and we are on index route, redirect to first conversation
          // this will also override redirectUrl set above, while keeping added viewId in params
          if (
            selectedLayout === 'chat' &&
            !params.conversationId &&
            firstConversation
          ) {
            url.pathname = `/dashboard/conversations/${firstConversation.id}`;
            redirectUrl = url;
          }

          if (redirectUrl) {
            return redirect(redirectUrl.toString());
          }

          return data;
        },
        element: <Outlet />,
        children: [
          {
            path: 'conversations',
            shouldRevalidate: shouldRevalidateDatatableLoader,
            lazy: () =>
              import(
                '@app/dashboard/conversations/conversations-table/conversations-table-page'
              ),
            loader: () =>
              queryClient.ensureQueryData(
                helpdeskQueries.attributes.normalizedList({
                  type: 'conversation',
                  for: 'agent',
                }),
              ),
          },
          {
            path: 'conversations/:conversationId',
            lazy: () =>
              import(
                '@app/dashboard/conversations/conversation-page/conversation-page'
              ),
            loader: async ({params}) => {
              return Promise.allSettled([
                queryClient.ensureQueryData(
                  helpdeskQueries.statuses.dropdownList('agent'),
                ),
                queryClient.ensureQueryData(
                  helpdeskQueries.conversations.get(params.conversationId!),
                ),
                queryClient.ensureInfiniteQueryData(
                  helpdeskQueries.conversations.messages(
                    params.conversationId!,
                  ),
                ),
              ]);
            },
          },
        ],
      },

      {element: <AuthRoute permission="users.update" />, children: customersRoutes},
      // Pages that also live under /admin (or /staff), mounted here so the
      // dashboard sidebar never throws staff into another layout. Each path
      // has its own rule in config/staff-access.php (StaffPageGate above).
      {
        element: <DashboardPageFrame />,
        children: [
          {path: 'students', lazy: () => import('@app/staff/staff-students-page')},
          // /my-desk (spec 06 §4) and the desk's control page; the roster.
          {path: 'mydesk', lazy: () => import('@app/staff/desk/my-desk-page')},
          {path: 'desk-control', lazy: () => import('@app/staff/desk/desk-control-page')},
          {path: 'roster', lazy: () => import('@app/staff/roster/staff-roster-page')},
          // Who saved which listing (owner, 2026-10-01).
          {path: 'favorites', lazy: () => import('@app/opportunities/admin/favorites-overview-page')},
          {path: 'abandoned-carts', lazy: () => import('@app/checkout/admin/abandoned-carts-page')},
          {path: 'service-requests', lazy: () => import('@app/services/admin/service-requests-datatable-page')},
          {path: 'volunteers', lazy: () => import('@app/volunteers/admin/volunteers-datatable-page')},
          {path: 'volunteers/:volunteerId', lazy: () => import('@app/volunteers/admin/volunteer-detail-page')},
          {path: 'partners', lazy: () => import('@app/services/admin/partners-page')},
          {path: 'testimonials', lazy: () => import('@app/appearance/content/content-page')},
          {path: 'advertisements', lazy: () => import('@app/advertisements/admin/advertisements-datatable-page')},
          {path: 'advertisements/new', lazy: () => import('@app/advertisements/admin/advertisement-form-page')},
          {path: 'advertisements/:adId/edit', lazy: () => import('@app/advertisements/admin/advertisement-form-page')},
        ],
      },
      ...viewsRoutes,
      ...campaignsRoutes,
      ...teamRoutes,
      {element: <AuthRoute permission="canned_replies.update" />, children: cannedRepliesRoutes(true)},
      {element: <AuthRoute permission="ai_agent.update" />, children: aiAgentRoutes},
    ],
     },
    ],
  },
];

/** /dashboard lands on the first area this person can open. */
